import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import {
  User, Building2, Link2, CreditCard, Bell, Users,
  Upload, Eye, EyeOff, Check,
  Mail, Shield, Clock, Globe, MapPin, Phone,
  Plug, Plus, Crown, Pencil
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/auth-context";
import { useVenue } from "@/hooks/use-venue";
import { useSearchParams } from 'react-router-dom';
import { useSubscription, useCreateCheckoutSession, useCreateBillingPortalSession } from '@/hooks/use-subscription';
import { useRollerIntegration, useSaveRollerConfig, useTestRollerConnection, useSyncRollerData } from '@/hooks/use-roller';

/* ───────── Profile Tab ───────── */
const ProfileTab = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const fileRef = useRef<HTMLInputElement>(null);
  const [avatar, setAvatar] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [profile, setProfile] = useState({
    firstName: "",
    lastName: "",
    email: user?.email ?? "",
    phone: "",
    role: user?.role ?? "",
  });

  useEffect(() => {
    if (user) {
      setProfile((prev) => ({
        ...prev,
        email: user.email,
        role: user.role,
      }));
    }
  }, [user]);

  const handleAvatarUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => setAvatar(ev.target?.result as string);
      reader.readAsDataURL(file);
    }
  };

  const initials = profile.firstName && profile.lastName
    ? `${profile.firstName[0]}${profile.lastName[0]}`
    : profile.email?.[0]?.toUpperCase() ?? "?";

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Your Profile</CardTitle>
          <CardDescription>Manage your personal details and how others see you.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center gap-6">
            <div className="relative group">
              <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary text-2xl font-bold text-primary-foreground overflow-hidden">
                {avatar ? (
                  <img src={avatar} alt="Avatar" className="h-full w-full object-cover" />
                ) : (
                  initials
                )}
              </div>
              <button
                onClick={() => fileRef.current?.click()}
                className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Upload className="h-5 w-5 text-white" />
              </button>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} />
            </div>
            <div>
              <p className="font-semibold text-lg">
                {profile.firstName || profile.lastName
                  ? `${profile.firstName} ${profile.lastName}`.trim()
                  : profile.email}
              </p>
              <p className="text-sm text-muted-foreground">{profile.role}</p>
            </div>
          </div>

          <Separator />

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">First Name</label>
              <Input
                value={profile.firstName}
                onChange={(e) => setProfile({ ...profile, firstName: e.target.value })}
                placeholder="First name"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Last Name</label>
              <Input
                value={profile.lastName}
                onChange={(e) => setProfile({ ...profile, lastName: e.target.value })}
                placeholder="Last name"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Email Address</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })} className="pl-9" />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Phone</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={profile.phone}
                  onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                  className="pl-9"
                  placeholder="+1 555 000 0000"
                />
              </div>
            </div>
          </div>

          <Separator />

          <div>
            <h4 className="text-sm font-semibold mb-3 flex items-center gap-2"><Shield className="h-4 w-4 text-primary" /> Change Password</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Current Password</label>
                <div className="relative">
                  <Input type={showPassword ? "text" : "password"} placeholder="••••••••" />
                  <button onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">New Password</label>
                <Input type={showPassword ? "text" : "password"} placeholder="••••••••" />
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={() => toast({ title: "Profile updated", description: "Your changes have been saved." })}>
              <Check className="h-4 w-4 mr-2" /> Save Changes
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

/* ───────── Venue Tab ───────── */
const VenueTab = () => {
  const { toast } = useToast();
  const { effectiveVenueId } = useAuth();
  const { data: venueData, isLoading } = useVenue(effectiveVenueId);

  const [venue, setVenue] = useState({
    name: "",
    address: "",
    timezone: "Australia/Melbourne",
    website: "",
    phone: "",
    currency: "AUD",
  });

  useEffect(() => {
    if (venueData) {
      setVenue((prev) => ({
        ...prev,
        name: venueData.name ?? "",
      }));
    }
  }, [venueData]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2"><Building2 className="h-5 w-5 text-primary" /> Venue Details</CardTitle>
          <CardDescription>Information about your venue displayed across the platform.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading venue details…</p>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2 col-span-2">
                  <label className="text-sm font-medium">Venue Name</label>
                  <Input value={venue.name} onChange={(e) => setVenue({ ...venue, name: e.target.value })} placeholder="Your venue name" />
                </div>
                <div className="space-y-2 col-span-2">
                  <label className="text-sm font-medium">Address</label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input value={venue.address} onChange={(e) => setVenue({ ...venue, address: e.target.value })} className="pl-9" placeholder="123 Main St, City" />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Website</label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input value={venue.website} onChange={(e) => setVenue({ ...venue, website: e.target.value })} className="pl-9" placeholder="https://yourvenue.com" />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Phone</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input value={venue.phone} onChange={(e) => setVenue({ ...venue, phone: e.target.value })} className="pl-9" placeholder="+1 555 000 0000" />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Timezone</label>
                  <Select value={venue.timezone} onValueChange={(v) => setVenue({ ...venue, timezone: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Australia/Melbourne">Australia/Melbourne (AEST)</SelectItem>
                      <SelectItem value="Australia/Sydney">Australia/Sydney (AEST)</SelectItem>
                      <SelectItem value="America/New_York">America/New York (EST)</SelectItem>
                      <SelectItem value="America/Los_Angeles">America/Los Angeles (PST)</SelectItem>
                      <SelectItem value="Europe/London">Europe/London (GMT)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Currency</label>
                  <Select value={venue.currency} onValueChange={(v) => setVenue({ ...venue, currency: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="AUD">AUD ($)</SelectItem>
                      <SelectItem value="USD">USD ($)</SelectItem>
                      <SelectItem value="GBP">GBP (£)</SelectItem>
                      <SelectItem value="EUR">EUR (€)</SelectItem>
                      <SelectItem value="NZD">NZD ($)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex justify-end pt-2">
                <Button onClick={() => toast({ title: "Venue updated", description: "Venue details saved successfully." })}>
                  <Check className="h-4 w-4 mr-2" /> Save Venue
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2"><Clock className="h-5 w-5 text-primary" /> Operating Hours</CardTitle>
          <CardDescription>Set your venue's regular operating hours for campaign scheduling.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((day) => {
              const isWeekend = day === "Saturday" || day === "Sunday";
              return (
                <div key={day} className="flex items-center gap-4">
                  <span className="w-28 text-sm font-medium">{day}</span>
                  <Input defaultValue={isWeekend ? "09:00" : "10:00"} className="w-24 text-sm" />
                  <span className="text-muted-foreground text-sm">to</span>
                  <Input defaultValue={isWeekend ? "21:00" : "20:00"} className="w-24 text-sm" />
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

/* ───────── Integrations Tab ───────── */
const IntegrationsTab = () => {
  const { toast } = useToast();
  const { effectiveVenueId } = useAuth();
  const { data: integration, refetch } = useRollerIntegration(effectiveVenueId ?? undefined);
  const saveConfig = useSaveRollerConfig(effectiveVenueId ?? '');
  const testConnection = useTestRollerConnection(effectiveVenueId ?? '');
  const syncData = useSyncRollerData(effectiveVenueId ?? '');

  const [showForm, setShowForm] = useState(!integration);
  const [formData, setFormData] = useState({ rollerApiUrl: '', rollerClientId: '', rollerClientSecret: '' });
  const [syncResults, setSyncResults] = useState<{ staff: unknown[]; revenue: unknown[]; syncedAt: string } | null>(null);
  const [testResult, setTestResult] = useState<{ connected: boolean; staffCount?: number; error?: string } | null>(null);
  const [showSyncResults, setShowSyncResults] = useState(false);

  useEffect(() => {
    if (integration) {
      setShowForm(false);
      setFormData({ rollerApiUrl: '', rollerClientId: '', rollerClientSecret: '' });
    }
  }, [integration]);

  return (
    <div className="space-y-6">
      {(showForm || !integration) && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><Plug className="h-5 w-5 text-primary" /> ROLLER API Configuration</CardTitle>
            <CardDescription>Enter your ROLLER API credentials to enable data synchronization.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">ROLLER API URL</label>
              <Input
                type="url"
                placeholder="https://api.odin.roller.app"
                value={formData.rollerApiUrl}
                onChange={(e) => setFormData({ ...formData, rollerApiUrl: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Client ID</label>
              <Input
                type="text"
                placeholder="Your Client ID"
                value={formData.rollerClientId}
                onChange={(e) => setFormData({ ...formData, rollerClientId: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Client Secret</label>
              <Input
                type="password"
                placeholder="Your Client Secret"
                value={formData.rollerClientSecret}
                onChange={(e) => setFormData({ ...formData, rollerClientSecret: e.target.value })}
              />
            </div>
            <Separator />
            <div className="flex justify-end">
              <Button
                onClick={() => {
                  saveConfig.mutate(formData, {
                    onSuccess: () => {
                      toast({ title: 'Config saved', description: 'Testing connection...' });
                      testConnection.mutate(undefined, {
                        onSuccess: (result) => {
                          setTestResult(result);
                          setShowForm(false);
                          refetch();
                          toast({
                            title: result.connected ? 'Connected!' : 'Connection failed',
                            description: result.connected ? `Found ${result.staffCount ?? 0} staff members` : result.error,
                            variant: result.connected ? 'default' : 'destructive'
                          });
                        },
                        onError: (err: unknown) => toast({ title: 'Test failed', description: (err as Error).message, variant: 'destructive' })
                      });
                    },
                    onError: (err: unknown) => toast({ title: 'Save failed', description: (err as Error).message, variant: 'destructive' })
                  });
                }}
                disabled={saveConfig.isPending || !formData.rollerApiUrl || !formData.rollerClientId || !formData.rollerClientSecret}
              >
                {saveConfig.isPending ? 'Saving...' : 'Save & Test Connection'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {integration && !showForm && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><Plug className="h-5 w-5 text-primary" /> ROLLER Integration</CardTitle>
            <CardDescription>Your ROLLER account is connected and ready to sync.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-lg border border-accent/30 bg-accent/10 p-4 space-y-3">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent/20">
                  <Check className="h-5 w-5 text-accent" />
                </div>
                <div>
                  <p className="font-semibold">Connected to ROLLER</p>
                  <p className="text-sm text-muted-foreground">Configuration saved and verified</p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="space-y-2">
                <label className="text-sm font-medium">API URL</label>
                <div className="rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground">{integration.rollerApiUrl}</div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Client ID</label>
                <div className="rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground">{integration.rollerClientId}</div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Client Secret</label>
                <div className="rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground">••••••••</div>
              </div>
            </div>

            {testResult && (
              <div className={`rounded-lg p-3 text-sm ${testResult.connected ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                {testResult.connected ? (
                  <p className="text-green-700 dark:text-green-400">✓ Connection successful. {testResult.staffCount} staff members found.</p>
                ) : (
                  <p className="text-red-700 dark:text-red-400">✗ {testResult.error}</p>
                )}
              </div>
            )}

            <Separator />

            <div className="flex gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  testConnection.mutate(undefined, {
                    onSuccess: (result) => {
                      setTestResult(result);
                      toast({
                        title: result.connected ? 'Connection OK' : 'Connection failed',
                        variant: result.connected ? 'default' : 'destructive'
                      });
                    },
                    onError: (err: unknown) => toast({ title: 'Test failed', description: (err as Error).message, variant: 'destructive' })
                  });
                }}
                disabled={testConnection.isPending}
              >
                {testConnection.isPending ? 'Testing...' : 'Test Connection'}
              </Button>
              <Button
                onClick={() => {
                  syncData.mutate(undefined, {
                    onSuccess: (result) => {
                      setSyncResults(result);
                      setShowSyncResults(true);
                      refetch();
                      toast({
                        title: 'Sync complete',
                        description: `${result.staff.length} staff, ${result.revenue.length} revenue entries synced`
                      });
                    },
                    onError: (err: unknown) => toast({ title: 'Sync failed', description: (err as Error).message, variant: 'destructive' })
                  });
                }}
                disabled={syncData.isPending}
              >
                {syncData.isPending ? 'Syncing...' : 'Sync Now'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowForm(true)}
              >
                Update Credentials
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {syncResults && showSyncResults && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Sync Results</CardTitle>
            <CardDescription>Last synced: {new Date(syncResults.syncedAt).toLocaleString()}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <details className="border rounded-lg p-3">
              <summary className="cursor-pointer font-medium text-sm">Staff ({syncResults.staff.length})</summary>
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b">
                    <tr>
                      <th className="text-left py-2 px-2">ID</th>
                      <th className="text-left py-2 px-2">First Name</th>
                      <th className="text-left py-2 px-2">Last Name</th>
                      <th className="text-left py-2 px-2">Role</th>
                      <th className="text-left py-2 px-2">Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {syncResults.staff.map((member: unknown, idx: number) => {
                      const m = member as Record<string, string>;
                      return (
                        <tr key={idx} className="border-b hover:bg-muted/30">
                          <td className="py-2 px-2 text-xs font-mono text-muted-foreground">{m.staffId}</td>
                          <td className="py-2 px-2">{m.firstName}</td>
                          <td className="py-2 px-2">{m.lastName}</td>
                          <td className="py-2 px-2"><Badge variant="outline">{m.role}</Badge></td>
                          <td className="py-2 px-2 text-xs text-muted-foreground">{m.email}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </details>

            <details className="border rounded-lg p-3">
              <summary className="cursor-pointer font-medium text-sm">Revenue ({syncResults.revenue.length})</summary>
              <div className="mt-3 space-y-2">
                {syncResults.revenue.slice(0, 10).map((item: unknown, idx: number) => (
                  <div key={idx} className="text-sm text-muted-foreground border-b pb-2 last:border-0">
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(item as Record<string, unknown>).map(([key, value]) => (
                        <div key={key}><span className="font-medium text-foreground">{key}:</span> {String(value)}</div>
                      ))}
                    </div>
                  </div>
                ))}
                {syncResults.revenue.length > 10 && (
                  <p className="text-xs text-muted-foreground italic">... and {syncResults.revenue.length - 10} more</p>
                )}
              </div>
            </details>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

/* ───────── Billing Tab ───────── */
const BillingTab = () => {
  const { toast } = useToast();
  const { effectiveVenueId } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: subscription, isLoading } = useSubscription(effectiveVenueId);
  const checkoutSession = useCreateCheckoutSession();
  const billingPortalSession = useCreateBillingPortalSession();

  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      toast({ title: 'Subscription activated!', description: 'Your plan has been upgraded successfully.' });
      searchParams.delete('success');
      setSearchParams(searchParams);
    } else if (searchParams.get('canceled') === 'true') {
      toast({ title: 'Checkout canceled', description: 'No changes were made to your subscription.', variant: 'destructive' });
      searchParams.delete('canceled');
      setSearchParams(searchParams);
    }
  }, []);

  useEffect(() => {
    if (checkoutSession.error) {
      toast({ title: 'Checkout failed', description: 'Unable to start checkout. Please try again.', variant: 'destructive' });
    }
  }, [checkoutSession.error]);

  useEffect(() => {
    if (billingPortalSession.error) {
      toast({ title: 'Portal unavailable', description: 'Unable to open billing portal. Please try again.', variant: 'destructive' });
    }
  }, [billingPortalSession.error]);

  const getStatusBadgeColor = (status: string | undefined) => {
    switch (status) {
      case 'active':
        return 'bg-green-500/15 text-green-700 dark:text-green-400';
      case 'trialing':
        return 'bg-blue-500/15 text-blue-700 dark:text-blue-400';
      case 'past_due':
        return 'bg-yellow-500/15 text-yellow-700 dark:text-yellow-400';
      case 'canceled':
      case 'unpaid':
      case 'incomplete':
        return 'bg-red-500/15 text-red-700 dark:text-red-400';
      default:
        return 'bg-gray-500/15 text-gray-700 dark:text-gray-400';
    }
  };

  const handleManageBilling = () => {
    if (effectiveVenueId) {
      billingPortalSession.mutateAsync({ venueId: effectiveVenueId });
    }
  };

  const handleSubscribeNow = () => {
    if (effectiveVenueId) {
      checkoutSession.mutateAsync({ venueId: effectiveVenueId });
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-muted-foreground">Loading subscription information…</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // No active subscription - show when subscription doesn't exist or is not active/trialing
  const hasActiveSubscription = subscription && (subscription.status === 'active' || subscription.status === 'trialing');

  if (!hasActiveSubscription) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><Crown className="h-5 w-5 text-primary" /> No Active Subscription</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">You don't have an active subscription yet. Subscribe now to unlock leaderboards and TV displays.</p>
            <Button onClick={handleSubscribeNow} disabled={checkoutSession.isPending} className="w-full">
              {checkoutSession.isPending ? 'Redirecting…' : 'Subscribe Now'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Active subscription
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2"><Crown className="h-5 w-5 text-primary" /> Current Plan</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between rounded-lg border border-primary/20 bg-primary/5 p-5">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-xl font-bold">Leaderboards</h3>
                <Badge className={getStatusBadgeColor(subscription.status)} variant="outline">
                  {subscription.status === 'trialing' ? 'Trialing' : subscription.status === 'active' ? 'Active' : subscription.status === 'past_due' ? 'Past Due' : subscription.status}
                </Badge>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">$40/mo per venue</p>
              {subscription.status === 'trialing' && subscription.trialEndDate && (
                <p className="mt-2 text-sm text-accent">Trial ends {new Date(subscription.trialEndDate).toLocaleDateString()}</p>
              )}
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold">
                $40
                <span className="text-sm font-normal text-muted-foreground">/mo</span>
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={handleManageBilling}
                disabled={billingPortalSession.isPending}
              >
                {billingPortalSession.isPending ? 'Opening…' : 'Manage Billing'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

/* ───────── Notifications Tab ───────── */
const NotificationsTab = () => {
  const { toast } = useToast();
  const [prefs, setPrefs] = useState({
    campaignStart: true,
    campaignEnd: true,
    dailySummary: true,
    milestoneAlerts: true,
    weeklyReport: false,
    newStaffSale: false,
    billingAlerts: true,
    productUpdates: false,
  });

  const toggle = (key: keyof typeof prefs) => setPrefs((p) => ({ ...p, [key]: !p[key] }));

  const items: { key: keyof typeof prefs; label: string; desc: string; category: string }[] = [
    { key: "campaignStart", label: "Campaign started", desc: "When a campaign goes live", category: "Campaigns" },
    { key: "campaignEnd", label: "Campaign ended", desc: "When a campaign finishes", category: "Campaigns" },
    { key: "milestoneAlerts", label: "Milestone reached", desc: "When a staff member hits a sales target", category: "Campaigns" },
    { key: "dailySummary", label: "Daily summary", desc: "Morning recap of the previous day's sales", category: "Reports" },
    { key: "weeklyReport", label: "Weekly report", desc: "Comprehensive weekly performance email", category: "Reports" },
    { key: "newStaffSale", label: "Every sale", desc: "Real-time notification for each sale recorded", category: "Reports" },
    { key: "billingAlerts", label: "Billing & invoices", desc: "Payment confirmations and upcoming charges", category: "Account" },
    { key: "productUpdates", label: "Product updates", desc: "New features and improvements", category: "Account" },
  ];

  const categories = [...new Set(items.map((i) => i.category))];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2"><Bell className="h-5 w-5 text-primary" /> Email Notifications</CardTitle>
          <CardDescription>Choose which email notifications you'd like to receive.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {categories.map((cat) => (
            <div key={cat}>
              <h4 className="text-xs font-semibold uppercase text-muted-foreground tracking-wider mb-3">{cat}</h4>
              <div className="space-y-3">
                {items.filter((i) => i.category === cat).map((item) => (
                  <div key={item.key} className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">{item.label}</p>
                      <p className="text-xs text-muted-foreground">{item.desc}</p>
                    </div>
                    <Switch checked={prefs[item.key]} onCheckedChange={() => toggle(item.key)} />
                  </div>
                ))}
              </div>
              <Separator className="mt-4" />
            </div>
          ))}
          <div className="flex justify-end">
            <Button onClick={() => toast({ title: "Preferences saved", description: "Your notification preferences have been updated." })}>
              <Check className="h-4 w-4 mr-2" /> Save Preferences
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

/* ───────── Team Tab ───────── */
const TeamTab = () => {
  const { toast } = useToast();
  const { user } = useAuth();

  const members = user
    ? [{ name: user.email, email: user.email, role: "Owner", avatar: user.email[0]?.toUpperCase() ?? "?" }]
    : [];

  const roleColors: Record<string, string> = {
    Owner: "bg-primary/15 text-primary",
    Admin: "bg-accent/15 text-accent",
    Viewer: "bg-muted text-muted-foreground",
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2"><Users className="h-5 w-5 text-primary" /> Team Members</CardTitle>
              <CardDescription>Manage who has access to your account.</CardDescription>
            </div>
            <Button size="sm" onClick={() => toast({ title: "Invite sent", description: "An invite form would appear here." })}>
              <Plus className="h-4 w-4 mr-1" /> Invite Member
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {members.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No team members yet.</p>
            ) : (
              members.map((m) => (
                <div key={m.email} className="flex items-center gap-4 rounded-lg border border-border p-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">{m.avatar}</div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">{m.name}</p>
                    <p className="text-xs text-muted-foreground">{m.email}</p>
                  </div>
                  <Badge className={`text-xs ${roleColors[m.role]}`}>{m.role}</Badge>
                  {m.role !== "Owner" && (
                    <Button variant="ghost" size="icon" className="text-muted-foreground"><Pencil className="h-4 w-4" /></Button>
                  )}
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Roles & Permissions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-sm">
            {[
              { role: "Owner", perms: "Full access. Manage billing, team, and all settings." },
              { role: "Admin", perms: "Create and manage campaigns, view reports, manage branding." },
              { role: "Viewer", perms: "View campaigns and reports only. No editing or settings access." },
            ].map((r) => (
              <div key={r.role} className="flex gap-3">
                <Badge className={`text-xs shrink-0 ${roleColors[r.role]}`}>{r.role}</Badge>
                <p className="text-muted-foreground">{r.perms}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

/* ───────── Main Settings Page ───────── */
const SettingsPage = () => {
  const tabs = [
    { value: "profile", label: "Profile", icon: User },
    { value: "venue", label: "Venue", icon: Building2 },
    { value: "integrations", label: "Integrations", icon: Link2 },
    { value: "billing", label: "Billing", icon: CreditCard },
    { value: "notifications", label: "Notifications", icon: Bell },
    { value: "team", label: "Team", icon: Users },
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground mt-1">Manage your account, venue, and preferences.</p>
      </div>

      <Tabs defaultValue="profile">
        <TabsList className="mb-6 flex-wrap h-auto gap-1">
          {tabs.map((t) => (
            <TabsTrigger key={t.value} value={t.value} className="gap-1.5">
              <t.icon className="h-4 w-4" /> {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
          <TabsContent value="profile"><ProfileTab /></TabsContent>
          <TabsContent value="venue"><VenueTab /></TabsContent>
          <TabsContent value="integrations"><IntegrationsTab /></TabsContent>
          <TabsContent value="billing"><BillingTab /></TabsContent>
          <TabsContent value="notifications"><NotificationsTab /></TabsContent>
          <TabsContent value="team"><TeamTab /></TabsContent>
        </motion.div>
      </Tabs>
    </div>
  );
};

export default SettingsPage;
